import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type BadgeCounterVariant =
  | 'button'
  | 'nav'
  | 'navsm'
  | 'tab-active'
  | 'tab-inactive'
  | 'incident'
  | 'incident-topbar'
  | 'notification-dot'
  | 'standalone'

const variantTokens = (
  variant: BadgeCounterVariant,
  colorScheme: ColorScheme
): { bg: string; color: string } => {
  const prefix = colorScheme === 'light' ? 'light.' : ''
  return {
    button: {
      bg: `${prefix}bg.badge-button`,
      color: `${prefix}text.badge-button`,
    },
    nav: { bg: `${prefix}bg.badge-nav`, color: `${prefix}text.badge-nav` },
    navsm: { bg: `${prefix}bg.badge-nav`, color: `${prefix}text.badge-nav` },
    'tab-active': {
      bg: `${prefix}bg.badge-nav`,
      color: `${prefix}text.badge-nav`,
    },
    'tab-inactive': {
      bg: `${prefix}bg.badge-button`,
      color: `${prefix}text.badge-nav`,
    },
    incident: {
      bg: `${prefix}bg.badge-incident`,
      color: `${prefix}text.badge-incident`,
    },
    'incident-topbar': {
      bg: `${prefix}status.incident-badge-topbar`,
      color: `${prefix}text.on-accent`,
    },
    'notification-dot': {
      bg: `${prefix}bg.notification-dot`,
      color: 'transparent',
    },
    standalone: {
      bg: `${prefix}bg.badge-button`,
      color: `${prefix}text.badge-button`,
    },
  }[variant]
}

export const notificationDotStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: 'full',
  bg:
    colorScheme === 'light'
      ? 'light.bg.notification-dot'
      : 'bg.notification-dot',
  flexShrink: 0,
})

export const badgeCounterStyles = (
  variant: BadgeCounterVariant = 'button',
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => {
  if (variant === 'notification-dot') {
    return notificationDotStyles(colorScheme)
  }

  const { bg, color } = variantTokens(variant, colorScheme)

  const baseStyles: SystemStyleObject = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    fontFamily: "'Soehne', sans-serif",
    fontWeight: '500',
    bg,
    color,
  }

  if (variant === 'navsm') {
    return {
      ...baseStyles,
      height: '18px',
      minWidth: '18px',
      borderRadius: '4px',
      fontSize: '12px',
      lineHeight: '16px',
      py: '1px',
      px: '4px',
    }
  }

  if (variant === 'tab-active' || variant === 'tab-inactive') {
    return {
      ...baseStyles,
      height: '24px',
      minWidth: '22px',
      borderRadius: '8px',
      fontFamily: "'Soehne Kraftig', sans-serif",
      fontSize: '15px',
      lineHeight: '24px',
      py: '1px',
      px: '5px',
    }
  }

  if (variant === 'standalone') {
    return {
      ...baseStyles,
      width: '28px',
      height: '28px',
      borderRadius: 'full',
      fontFamily: "'Soehne Kraftig', sans-serif",
      fontSize: '11px',
      lineHeight: '16px',
    }
  }

  return {
    ...baseStyles,
    height: '22px',
    minWidth: '24px',
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '20px',
    px: '5px',
  }
}
